import { useEffect, useState } from 'react';
import { getUsage } from '../services/api';
import { AlertCircle } from 'lucide-react';

// Global refresh trigger for quota updates
let quotaRefreshCallback = null;

export function triggerQuotaRefresh() {
  if (quotaRefreshCallback) {
    quotaRefreshCallback();
  }
}

export default function QuotaCard({ isCollapsed, isHighlighted = true }) {
  const [quotaData, setQuotaData] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // isInitial gates the loading state so background refreshes don't blank the card.
  const fetchUsageData = async (isInitial = false) => {
    try {
      setError(null);
      if (isInitial) setLoading(true);
      const usage = await getUsage();
      setQuotaData(usage.quota);
      setTokenData(usage.tokens);
    } catch (err) {
      setError(err.message || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsageData(true);

    // Quota only changes when the user sends a message, so refresh on that
    // event only — no polling.
    quotaRefreshCallback = () => fetchUsageData(false);

    return () => {
      quotaRefreshCallback = null;
    };
  }, []);

  if (isCollapsed) {
    return null;
  }

  const wrapperClassName = isHighlighted
    ? 'mb-4 rounded-lg p-3 transition border bg-light-btn border-light-border dark:bg-sidebar/50 dark:border-glass'
    : 'mb-4 rounded-lg transition';

  if (error) {
    return (
      <div className={wrapperClassName}>
        <div className="text-xs text-red-500">Usage data unavailable</div>
      </div>
    );
  }

  if (loading || !quotaData || !tokenData) {
    return (
      <div className={wrapperClassName}>
        <div className="text-xs transition text-light-muted dark:text-secondary">Loading usage...</div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${year}/${month}/${day}`;
  };

  const quotaPercent = Math.round((quotaData.used / quotaData.limit) * 100);
  const tokenPercent = Math.round(
    (tokenData.total_used / tokenData.limit) * 100
  );

  const quotaReached = quotaData.remaining <= 0;
  const tokenReached = tokenData.remaining <= 0;
  const quotaWarning = quotaPercent >= 80;
  const tokenWarning = tokenPercent >= 80;
  const limitReached = quotaReached || tokenReached;

  return (
    <div className={wrapperClassName}>
      <div className="text-xs font-semibold mb-3 transition text-light-muted dark:text-secondary">
        Usage
      </div>

      {/* API Calls */}
      <div className="mb-3">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs transition text-light-text dark:text-primary">
            API Calls
          </span>
          <span className={`text-xs font-medium transition ${quotaWarning ? 'text-red-500' : 'text-light-text dark:text-primary'}`}>
            {quotaData.used}/{quotaData.limit}
          </span>
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden transition bg-black/10 dark:bg-white/10">
          <div
            className={`h-full transition-all ${
              quotaWarning ? 'bg-red-500' : 'bg-emerald-500'
            }`}
            style={{ width: `${Math.min(quotaPercent, 100)}%` }}
          />
        </div>
      </div>

      {/* Tokens */}
      <div className="mb-3">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs transition text-light-text dark:text-primary">
            Tokens
          </span>
          <span className={`text-xs font-medium transition ${tokenWarning ? 'text-red-500' : 'text-light-text dark:text-primary'}`}>
            {tokenData.total_used?.toLocaleString()}
            /{tokenData.limit?.toLocaleString()}
          </span>
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden transition bg-black/10 dark:bg-white/10">
          <div
            className={`h-full transition-all ${
              tokenWarning ? 'bg-red-500' : 'bg-blue-500'
            }`}
            style={{ width: `${Math.min(tokenPercent, 100)}%` }}
          />
        </div>
      </div>

      {/* Reset Date */}
      <div className="flex items-center justify-between text-xs transition text-light-muted dark:text-secondary">
        <span>Reset:</span>
        <span className="font-medium">{formatDate(tokenData.reset_date)}</span>
      </div>

      {/* Warning */}
      {(quotaWarning || tokenWarning) && (
        <div className="mt-2 flex items-center gap-2 p-2 rounded transition bg-red-50 dark:bg-red-500/10">
          <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
          <span className="text-xs text-red-600">
            {limitReached
              ? 'Monthly limit reached — resets next month'
              : 'Approaching limit'}
          </span>
        </div>
      )}
    </div>
  );
}
